// src/components/UpdatePrompt.tsx
//
// iSentinel360 v0.4.6h — combined PWA + APK update prompt
//                       (with v0.4.6i BUG FIX)
//
// This component handles BOTH update channels in one place:
//
//   1. PWA updates via the existing service worker
//      (registered by vite-plugin-pwa's useRegisterSW hook).
//      Banner at the BOTTOM of the screen.
//
//   2. APK updates via the new native bridge
//      (window.Android.checkForApkUpdate).
//      Banner at the TOP of the screen.
//
//   3. Chrome install modal (v0.4.6d–h) — shown when a user on
//      Android Chrome (not in the APK) clicks "Check for
//      updates" in the hamburger menu. This is the "manual"
//      install path. The automatic first-login install prompt
//      is now handled by ChromeInstallRedirect.tsx (v0.4.6i).
//
// =============================================================
// v0.4.6i BUG FIX (2026-08-28):
//
// The previous version (v0.4.6h) used the relative URL
// `/api/apk-version` in fetchChromeApkInfo. The PWA is hosted
// at app.isentinel360.com, so the relative URL resolved to
// app.isentinel360.com/api/apk-version — which doesn't exist.
// The fetch returned 404, chromeError was set, and the modal
// showed "Retry" forever (clicking "Retry" re-fetched the
// same broken URL). The user could never install the APK
// because the "Install" button was never rendered.
//
// The fix is to use the absolute URL
// `https://api.isentinel360.com/api/apk-version`, which is the
// real API server. This is the same fix applied to
// useFirstInstall.ts in this delivery.
// =============================================================

import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useTranslation } from 'react-i18next';
import { X, Download, AlertTriangle, Smartphone } from 'lucide-react';
import { useApkUpdate } from '../hooks/useApkUpdate';
import { usePlayProtectImage } from '../hooks/usePlayProtectImage';

// Detect Android mobile (not iOS, not Android tablet).
function isAndroidMobileUA(ua: string): boolean {
  return /Android/i.test(ua) && /Mobile/i.test(ua);
}

// Detect iOS (iPhone, iPad, iPod). Used so the "Check for updates"
// flow on iOS doesn't show the APK toast (iOS has no APK) and
// doesn't show the Chrome install modal (iOS doesn't use the APK).
function isIOSUA(ua: string): boolean {
  return /iPhone|iPad|iPod/i.test(ua);
}

import { UpdateModal } from './UpdateModal';

export default function UpdatePrompt() {
  const { t } = useTranslation();

  // =====================================================================
  // PWA update (v0.4.4 — preserved exactly, do not modify without care)
  // =====================================================================
  const [showPwaPrompt, setShowPwaPrompt] = useState(false);
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(_r) {},
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      setShowPwaPrompt(true);
    }
  }, [needRefresh]);

  const handlePwaUpdate = async () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const registration = await navigator.serviceWorker.ready;
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      window.location.reload();
    });
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // =====================================================================
  // APK update (v0.4.5)
  // =====================================================================
  const apkUpdate = useApkUpdate();
  const playProtectWarning = usePlayProtectImage();
  const [showApkModal, setShowApkModal] = useState(false);
  const [showChromeInstallModal, setShowChromeInstallModal] = useState(false);

  // v0.4.6h: local state for the Chrome install modal.
  const [chromeApkInfo, setChromeApkInfo] = useState<{
    versionName: string;
    downloadUrl: string;
  } | null>(null);
  const [chromeLoading, setChromeLoading] = useState(false);
  const [chromeError, setChromeError] = useState<string | null>(null);

  // =====================================================================
  // v0.4.6i BUG FIX:
  //   Changed the fetch URL from the relative '/api/apk-version'
  //   (which from app.isentinel360.com resolves to the wrong
  //   origin and 404s, leaving the modal stuck on "Retry") to
  //   the absolute 'https://api.isentinel360.com/api/apk-version'.
  //   The rest of the function is unchanged.
  // =====================================================================
  const fetchChromeApkInfo = async () => {
    setChromeLoading(true);
    setChromeError(null);
    try {
      const res = await fetch(
        'https://api.isentinel360.com/api/apk-version',
        { credentials: 'omit' }
      );
      if (!res.ok) {
        setChromeError(`HTTP ${res.status}`);
        setChromeApkInfo(null);
        return;
      }
      const data = await res.json();
      if (!data.downloadUrl) {
        setChromeError('no_download_url');
        setChromeApkInfo(null);
        return;
      }
      setChromeApkInfo({ versionName: data.versionName, downloadUrl: data.downloadUrl });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setChromeError(msg);
      setChromeApkInfo(null);
    } finally {
      setChromeLoading(false);
    }
  };

  useEffect(() => {
    if (showChromeInstallModal) {
      setChromeApkInfo(null);
      setChromeError(null);
      setChromeLoading(true);
      fetchChromeApkInfo();
    } else {
      setChromeApkInfo(null);
      setChromeError(null);
      setChromeLoading(false);
    }
  }, [showChromeInstallModal]);

  // =====================================================================
  // Manual check (triggered by HamburgerMenu "Check for updates" item)
  //
  // v0.4.6i: only show the "Checking for updates" toast when the
  // user is IN the APK on Android. On desktop / iOS / Chrome-on-
  // mobile, the toast was contradictory: it said "You are on the
  // latest version" (no APK update available because there's no
  // APK on desktop) while the PWA banner at the bottom said
  // "A new version is available — click Update" (a PWA update).
  //
  // New flow:
  //   1. Android mobile in Chrome (not in APK) → open the install
  //      modal (which has its own loading state). No toast.
  //   2. In APK on Android → check for APK update, show toast.
  //   3. Desktop / iOS / other → silently trigger PWA service-
  //      worker update; the PWA banner at the bottom appears if
  //      there's a PWA update. No toast.
  // =====================================================================
  const [checkToast, setCheckToast] = useState<{
    message: string;
    nonce: number;
  } | null>(null);

  useEffect(() => {
    const handler = () => {
      const inApk = typeof window !== 'undefined' && !!(window as any).Android;
      const ua = navigator.userAgent || '';
      const isAndroidMobile = isAndroidMobileUA(ua);

      // 1) Android mobile in Chrome (not in APK) → install modal.
      if (!inApk && isAndroidMobile) {
        setShowChromeInstallModal(true);
        setCheckToast(null);
        return;
      }

      // 2) PWA service worker update (silent — banner shows if avail).
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then((reg) => {
          if (reg) reg.update();
        });
      }

      // 3) APK update toast: ONLY when in the APK on Android mobile.
      //    Desktop, iOS, and others get no toast.
      if (inApk && isAndroidMobile) {
        apkUpdate.check();
        setCheckToast({ message: t('update.checking'), nonce: Date.now() });
      } else {
        setCheckToast(null);
      }
    };
    window.addEventListener('is360-check-updates', handler as EventListener);
    return () =>
      window.removeEventListener('is360-check-updates', handler as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apkUpdate.check, t]);

  useEffect(() => {
    if (!checkToast) return;
    if (checkToast.message !== t('update.checking')) return;
    const id = setTimeout(() => {
      if (apkUpdate.update?.error) {
        setCheckToast({ message: t('update.checkFailed'), nonce: Date.now() });
      } else if (apkUpdate.update?.updateAvailable) {
        const msg = apkUpdate.update.mandatory
          ? t('update.toastMandatory', { version: apkUpdate.update.versionName })
          : t('update.toastAvailable', { version: apkUpdate.update.versionName });
        setCheckToast({ message: msg, nonce: Date.now() });
      } else {
        setCheckToast({ message: t('update.toastUpToDate'), nonce: Date.now() });
      }
    }, 1500);
    return () => clearTimeout(id);
  }, [checkToast, apkUpdate.update, t]);

  useEffect(() => {
    if (!checkToast) return;
    if (checkToast.message === t('update.checking')) return;
    const id = setTimeout(() => {
      setCheckToast((current) =>
        current && current.nonce === checkToast.nonce ? null : current,
      );
    }, 3000);
    return () => clearTimeout(id);
  }, [checkToast, t]);

  // =====================================================================
  // Render
  // =====================================================================
  const showApkBanner = !!apkUpdate.update?.updateAvailable;

  return (
    <>
      {/* ─── APK update banner (top) ────────────────────────────── */}
      {showApkBanner && apkUpdate.update && (
        <div
          role="status"
          aria-live="polite"
          className={[
            'fixed top-4 left-4 right-4 z-50',
            'flex items-center justify-between gap-3 px-4 py-3 rounded-xl shadow-lg',
            'border text-sm',
            apkUpdate.update.mandatory
              ? 'bg-red-50 text-red-900 border-red-300 dark:bg-red-950/40 dark:text-red-100 dark:border-red-800'
              : 'bg-amber-50 text-amber-900 border-amber-300 dark:bg-amber-950/40 dark:text-amber-100 dark:border-amber-800',
          ].join(' ')}
        >
          <div className="flex items-center gap-2 min-w-0">
            {apkUpdate.update.mandatory ? (
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            ) : null}
            <p className="font-medium truncate">
              {apkUpdate.update.mandatory
                ? t('update.mandatory', { version: apkUpdate.update.versionName })
                : t('update.available', { version: apkUpdate.update.versionName })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowApkModal(true)}
              className={[
                'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                apkUpdate.update.mandatory
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700',
              ].join(' ')}
            >
              <Download className="w-3.5 h-3.5" />
              {t('update.updateButton')}
            </button>
            {!apkUpdate.update.mandatory && (
              <button
                onClick={apkUpdate.dismiss}
                aria-label={t('update.dismissAriaLabel')}
                className="p-1 rounded hover:bg-black/10"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── PWA update banner (bottom) ─────────────────────────── */}
      {showPwaPrompt && (
        <div className="fixed bottom-4 left-4 right-4 z-50 flex items-center justify-between gap-4 rounded-xl bg-card border border-border p-4 shadow-lg animate-in slide-in-from-bottom">
          <div>
            {/* v0.4.6i: reworded the PWA banner to make it clear
                this is a WEBSITE / PWA update, NOT the Android
                APK. Previously the banner said "A new version is
                available" which the user perceived as
                contradictory with the desktop "You are on the
                latest version" APK toast. The new wording makes
                the difference explicit ("website", not "app"). */}
            <p className="font-semibold text-sm">{t('update.pwaTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('update.pwaSubtitle')}</p>
          </div>
          <div className="flex gap-2">
            <button
              className="px-4 py-2 text-xs font-medium rounded-md bg-muted hover:bg-accent"
              onClick={() => setShowPwaPrompt(false)}
            >
              {t('update.dismiss')}
            </button>
            <button
              className="px-4 py-2 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handlePwaUpdate}
            >
              {t('update.updateButton')}
            </button>
          </div>
        </div>
      )}

      {/* ─── APK update modal ───────────────────────────────────── */}
      {showApkModal && apkUpdate.update && (
        <UpdateModal
          update={apkUpdate.update}
          onClose={() => setShowApkModal(false)}
          install={apkUpdate.install}
          openSettings={apkUpdate.openUnknownAppsSettings}
          installing={apkUpdate.installing}
          installError={apkUpdate.installError}
        />
      )}

      {/* ─── Chrome install modal (v0.4.6d) — bug fix v0.4.6i ──── */}
      {showChromeInstallModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="is360-chrome-install-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowChromeInstallModal(false);
          }}
        >
          <div className="bg-background rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 relative my-8">
            <button
              onClick={() => setShowChromeInstallModal(false)}
              aria-label={t('update.dismissAriaLabel')}
              className="absolute top-3 right-3 p-1 rounded hover:bg-muted"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3 mb-4">
              <Smartphone className="w-7 h-7 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h2 id="is360-chrome-install-title" className="text-lg font-bold">
                  {chromeApkInfo
                    ? t('install.bannerTitle', { version: chromeApkInfo.versionName })
                    : t('install.bannerTitleNoVersion')}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('install.bannerBody')}
                </p>
              </div>
            </div>

            <ol className="space-y-2 text-sm mb-5 list-decimal pl-5 marker:text-muted-foreground">
              <li>{t('update.step1')}</li>
              <li>{t('update.step2')}</li>
              <li>{t('update.step3')}</li>
              <li>{t('update.step4')}</li>
            </ol>

            <div className="mb-3 border rounded-lg overflow-hidden bg-gray-50 dark:bg-gray-800">
              <img
                src={playProtectWarning}
                alt={t('update.playProtectAlt')}
                className="w-full h-auto"
              />
            </div>

            <p className="text-xs text-muted-foreground mb-4">
              {t('update.warningIntro')}
            </p>

            {chromeError ? (
              <button
                type="button"
                onClick={() => fetchChromeApkInfo()}
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md text-sm font-medium bg-destructive text-white hover:bg-destructive/90 transition-colors"
              >
                {t('install.retry')}
              </button>
            ) : chromeLoading && !chromeApkInfo ? (
              <button
                type="button"
                disabled
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md text-sm font-medium bg-blue-400 text-white cursor-not-allowed"
              >
                <span className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                {t('common.loading') || 'Loading…'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  const url = chromeApkInfo?.downloadUrl;
                  if (url) {
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }
                }}
                disabled={!chromeApkInfo?.downloadUrl}
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                {t('install.installButton')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Manual check result toast ──────────────────────────── */}
      {checkToast && (
        <div
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-lg shadow-2xl text-sm font-medium max-w-[90vw] bg-gray-800 text-white"
          role="status"
          aria-live="polite"
        >
          {checkToast.message}
        </div>
      )}
    </>
  );
}
